import { AGMNewSkuInput, RequestPartNumberStatus } from 'src/API';
import { ChangeEntityType, ChangeSectionName, ChangeOperationType } from 'src/types/change-history/changeHistory.types';
import { logChangeHistory } from 'src/utils/changeHistoryUtils';
import { AssortmentRowWithMetadata } from 'src/components/manage-programs/rpn-fpn/utils/assortmentUtils';
import {
  createAGMSku,
  createOrUpdateAGMAssortments,
  createOrUpdateAGMVersions,
  deactivateAGMSku,
  deleteAGMAssortments,
  reactivateAGMSku,
  request_pn,
  unlockSkuOverride,
  updateAGMAttributeOrder,
  updateAGMSkuAssortments,
  updateAGMSkuMarketplaces,
  updateAGMSkuVersionType,
} from 'src/graphql/mutations';
import {
  listAGMVersionsAttributeOrder,
  listMarketplaces,
  listRPNAssortments,
  listRPNSkus,
  listRPNVersions,
} from 'src/graphql/queries';
import {
  Marketplace,
  UpdateSkuMarketplacesInput,
  UpdateSkuMarketplacesResponse,
} from 'src/types/rpn/marketplace.types';
import {
  AgmVersionAttributeOrderApiResponse,
  RpnAssortment,
  RpnVersion,
  RpnVersionInput,
  Skus,
} from 'src/types/rpn/rpnDetails.type';
import { formatApiError, getErrorMessage } from '../../utils/errorUtils';
import { logger } from '../../utils/logger';
import { parseTownsendError } from '../../utils/townsendErrorParser';
import { fetchGraphQLRequest } from './clients/graphqlClient';

/**
 * AGM (ASIN Generation Management) Service
 * Handles all AGM-related API operations including versions, assortments, and SKUs
 */

// ============================================================================
// AGM Versions Attribute Order Operations
// ============================================================================

/**
 * Fetches AGM Versions Attribute Order by product line ID and RPN ID
 * @param productLineId - The ID of the product line
 * @param rpnId - The ID of the RPN
 * @returns Promise resolving to AGM version attribute order details
 * @throws Error if API call fails
 *
 */
export const fetchAGMVersionsAttributeOrder = async (
  productLineId: number,
  rpnId: number
): Promise<AgmVersionAttributeOrderApiResponse> => {
  try {
    logger.info(`Fetching AGM Versions Attribute Order for product line ${productLineId}, RPN ${rpnId} from API`);

    const listAGMVersionsAttributeOrderResult = await fetchGraphQLRequest<{
      listAGMVersionsAttributeOrder: AgmVersionAttributeOrderApiResponse;
    }>({
      query: listAGMVersionsAttributeOrder,
      variables: { input: { rpn_id: rpnId } },
      requestName: 'listAGMVersionsAttributeOrder',
      requestType: 'query',
    });

    const agmVersionAttributeOrderResponse = listAGMVersionsAttributeOrderResult.listAGMVersionsAttributeOrder;

    logger.info(`Fetched AGM Versions Attribute Order for product line ${productLineId}, RPN ${rpnId} from API`);
    return agmVersionAttributeOrderResponse;
  } catch (error) {
    logger.error(`Error fetching AGM Versions Attribute Order for product line ${productLineId}, RPN ${rpnId}:`, {
      error,
    });
    throw formatApiError('Failed to fetch AGM Versions Attribute Order', error);
  }
};

/**
 * Updates AGM Attribute Order for a specific RPN
 * @param rpnId - The ID of the RPN
 * @param attributeOrders - Array of attribute types with their order positions
 * @returns Promise resolving to success status and message
 * @throws Error if API call fails
 */
export const updateAGMVersionsAttributeOrder = async (
  rpnId: number,
  attributeOrders: { attribute_type: string; order: number }[]
): Promise<{ success: boolean; message: string; lastSucceededIndex?: number | null }> => {
  try {
    logger.info(`Updating AGM Attribute Order for RPN ${rpnId}`, {
      attributeCount: attributeOrders.length,
      attributes: attributeOrders.map((a) => a.attribute_type),
    });

    const result = await fetchGraphQLRequest<{
      updateAGMAttributeOrder: {
        body?: string | null;
        error?: string | null;
        lastSucceededIndex?: number | null;
        statusCode?: number | null;
      };
    }>({
      query: updateAGMAttributeOrder,
      variables: {
        input: {
          rpn_id: rpnId,
          attribute_orders: attributeOrders,
        },
      },
      requestName: 'updateAGMAttributeOrder',
      requestType: 'mutation',
    });

    const response = result.updateAGMAttributeOrder;

    if (response.error || (response.statusCode && response.statusCode >= 400)) {
      throw new Error(response.error || `Failed with status code: ${response.statusCode}`);
    }

    logger.info(`Successfully updated AGM Attribute Order for RPN ${rpnId}`, {
      lastSucceededIndex: response.lastSucceededIndex,
    });

    return {
      success: true,
      message: `Successfully updated attribute order for RPN ${rpnId}`,
      lastSucceededIndex: response.lastSucceededIndex,
    };
  } catch (error) {
    logger.error(`Error updating AGM Attribute Order for RPN ${rpnId}:`, { error });
    throw formatApiError('Failed to update AGM Attribute Order', error);
  }
};

// ============================================================================
// AGM Version Operations
// ============================================================================

/**
 * Creates, updates, or deletes RPN versions
 * @param productLineId - The ID of the product line
 * @param rpnId - The ID of the RPN
 * @param versions - Array of version data to create
 * @returns Promise resolving to the created versions response
 * @throws Error if API call fails
 */
export const createRpnVersionsAPI = async (
  productLineId: number,
  rpnId: number,
  versions: RpnVersionInput[],
  /** Map of version_id → previous version_description for accurate change history old_value */
  oldVersionsMap?: Record<number, { version_description: string }>
): Promise<{ success: boolean; lastSucceededIndex: number | null }> => {
  try {
    // Count creates vs deletes for accurate logging
    const createCount = versions.filter((v) => v.is_active).length;
    const deleteCount = versions.filter((v) => !v.is_active).length;

    logger.info(`Processing ${versions.length} AGM versions for product line ${productLineId}, RPN ${rpnId}`, {
      createCount,
      deleteCount,
      versions,
    });

    // Transform RpnVersionInput to AGMVersionInput format for the mutation
    // AGMVersionInput schema:
    // - item_metadata: ItemMetadataInput! (required)
    // - rpn_id: Int! (required)
    // - version_config: AWSJSON! (required)
    // - version_description: String (optional)
    // - version_id: Int (optional - undefined for new rows)
    const transformedVersions = versions.map((version) => ({
      item_metadata: {
        created_at: version.updated_at,
        created_by: version.updated_by,
        is_active: version.is_active,
        updated_at: version.updated_at,
        updated_by: version.updated_by,
      },
      rpn_id: version.rpn_id,
      version_config: version.version_config,
      version_description: version.version_description || undefined,
      // Convert null to undefined for new rows (GraphQL expects undefined/omitted for optional fields)
      version_id: version.version_id ?? undefined,
    }));

    const result = await fetchGraphQLRequest<{
      createOrUpdateAGMVersions: {
        body?: string | null;
        error?: string | null;
        lastSucceededIndex?: number | null;
        statusCode?: number | null;
      };
    }>({
      query: createOrUpdateAGMVersions,
      variables: { input: { versions: transformedVersions } },
      requestName: 'createOrUpdateAGMVersions',
      requestType: 'mutation',
    });

    const response = result.createOrUpdateAGMVersions;

    if (response.error || (response.statusCode && response.statusCode >= 400)) {
      throw new Error(response.error || `Failed with status code: ${response.statusCode}`);
    }

    logger.info(`Successfully created/updated AGM versions for product line ${productLineId}, RPN ${rpnId}`, {
      lastSucceededIndex: response.lastSucceededIndex,
    });

    // Fire-and-forget: log change history for version changes
    logChangeHistory({
      entityType: ChangeEntityType.RPN,
      rpnId,
      productLineId,
      sectionName: ChangeSectionName.AGM_VERSIONS,
      operationType: ChangeOperationType.UPDATE,
      createdBy: versions[0]?.updated_by || 'unknown',
      fieldChanges: versions.map((v) => ({
        field_name: `version_${v.version_id || 'new'}`,
        field_label: `${v.version_description || 'Version'} (${!v.version_id ? 'added' : v.is_active ? 'updated' : 'deleted'})`,
        old_value: v.version_id ? (oldVersionsMap?.[v.version_id]?.version_description ?? null) : null,
        new_value: v.is_active ? v.version_description || null : null,
      })),
    });

    return { success: true, lastSucceededIndex: response.lastSucceededIndex ?? null };
  } catch (error) {
    logger.error(`Error creating AGM versions for product line ${productLineId}, RPN ${rpnId}:`, { error });
    throw formatApiError('Failed to create AGM versions', error);
  }
};

// ============================================================================
// AGM Assortment Operations
// ============================================================================

/**
 * Creates or updates assortments for an RPN
 * @param productLineId - The ID of the product line
 * @param rpnId - The ID of the RPN
 * @param fpnId - The ID of the FPN (unused, kept for backward compatibility)
 * @param assortments - Array of assortment data with metadata to create
 * @returns Promise resolving to the created assortments response
 * @throws Error if API call fails
 */
export const updateAssortments = async (
  productLineId: number,
  rpnId: number,
  fpnId: number,
  assortments: AssortmentRowWithMetadata[]
): Promise<{ success: boolean; lastSucceededIndex: number | null }> => {
  try {
    logger.info(
      `Creating ${assortments.length} new assortments for product line ${productLineId}, RPN ${rpnId}, FPN ${fpnId}`,
      { assortments }
    );

    // Transform AssortmentRowWithMetadata to AGMAssortmentInput format for the mutation
    // AGMAssortmentInput schema:
    // - assortment_channel: String! (required)
    // - assortment_country: String! (required)
    // - assortment_id: Int (optional - undefined for new rows)
    // - item_metadata: ItemMetadataInput (optional)
    // - marketplace: String (optional)
    // - rpn_id: Int! (required)
    // - sku_type: String! (required)
    // - status: String (optional)
    // - version_description: String (optional)
    // - version_id: Int! (required)
    const transformedAssortments = assortments.map((assortment) => ({
      assortment_channel: assortment.channel,
      assortment_country: assortment.country,
      assortment_id: undefined, // New assortments don't have an ID
      item_metadata: assortment.item_metadata,
      marketplace: undefined, // Optional field
      rpn_id: rpnId,
      sku_type: assortment.sku_type,
      status: undefined, // Optional field
      version_description: assortment.version_description || undefined,
      version_id: assortment.version_id,
    }));

    const result = await fetchGraphQLRequest<{
      createOrUpdateAGMAssortments: {
        body?: string | null;
        error?: string | null;
        lastSucceededIndex?: number | null;
        statusCode?: number | null;
      };
    }>({
      query: createOrUpdateAGMAssortments,
      variables: { input: { assortments: transformedAssortments } },
      requestName: 'createOrUpdateAGMAssortments',
      requestType: 'mutation',
    });

    const response = result.createOrUpdateAGMAssortments;

    if (response.error || (response.statusCode && response.statusCode >= 400)) {
      throw new Error(response.error || `Failed with status code: ${response.statusCode}`);
    }

    logger.info(`Successfully created/updated assortments for product line ${productLineId}, RPN ${rpnId}`, {
      lastSucceededIndex: response.lastSucceededIndex,
    });

    // Fire-and-forget: log change history for assortment changes
    logChangeHistory({
      entityType: ChangeEntityType.RPN,
      rpnId,
      productLineId,
      sectionName: ChangeSectionName.AGM_ASSORTMENTS,
      operationType: ChangeOperationType.UPDATE,
      createdBy: assortments[0]?.item_metadata?.created_by || 'unknown',
      fieldChanges: assortments.map((a) => ({
        field_name: 'assortment',
        field_label: `${a.version_description || `Version ${a.version_id}`} (added)`,
        old_value: null,
        new_value: `COUNTRY → ${a.country}, CHANNEL → ${a.channel}, SKU Type → ${a.sku_type}`,
      })),
    });

    return { success: true, lastSucceededIndex: response.lastSucceededIndex ?? null };
  } catch (error) {
    logger.error(`Error creating assortments for product line ${productLineId}, RPN ${rpnId}, FPN ${fpnId}:`, {
      error,
    });
    throw formatApiError('Failed to create assortments', error);
  }
};

/**
 * Deletes multiple AGM assortments by IDs in a single batch API call
 * @param assortmentIds - Array of assortment IDs to delete
 * @returns Promise resolving to results with success count and any failures
 * @throws Error if the deletion fails
 */
export const deleteAGMAssortmentsAPI = async (
  assortmentIds: number[]
): Promise<{ successCount: number; failedIds: number[] }> => {
  if (assortmentIds.length === 0) {
    return { successCount: 0, failedIds: [] };
  }

  try {
    logger.info(`Deleting ${assortmentIds.length} AGM assortment(s)`, { assortmentIds });

    const result = await fetchGraphQLRequest<{
      deleteAGMAssortments: {
        body?: string | null;
        error?: string | null;
        lastSucceededIndex?: number | null;
        statusCode?: number | null;
      };
    }>({
      query: deleteAGMAssortments,
      variables: { input: { assortment_ids: assortmentIds } },
      requestName: 'deleteAGMAssortments',
      requestType: 'mutation',
    });

    const response = result.deleteAGMAssortments;

    if (response.error || (response.statusCode && response.statusCode >= 400)) {
      throw new Error(response.error || `Failed with status code: ${response.statusCode}`);
    }

    logger.info(`Successfully deleted ${assortmentIds.length} AGM assortment(s)`, {
      lastSucceededIndex: response.lastSucceededIndex,
    });

    return { successCount: assortmentIds.length, failedIds: [] };
  } catch (error) {
    logger.error(`Error deleting AGM assortments:`, { assortmentIds, error });
    throw formatApiError('Failed to delete AGM assortments', error);
  }
};

// ============================================================================
// Targeted AGM Data Fetch Operations (for post-mutation refetch)
// ============================================================================

/**
 * Fetches only AGM versions from AGM data
 * Used for targeted refetch after createOrUpdateAGMVersions mutation
 * @param rpnId - The ID of the RPN
 * @returns Promise resolving to array of AGM versions
 * @throws Error if API call fails
 */
export const fetchRPNAGMVersionsAPI = async (rpnId: number): Promise<RpnVersion[]> => {
  try {
    logger.info(`Fetching AGM Versions for RPN ${rpnId} from API`);

    const result = await fetchGraphQLRequest<{
      listRPNVersions: RpnVersion[];
    }>({
      query: listRPNVersions,
      variables: { input: { rpn_id: rpnId } },
      requestName: 'listRPNVersions',
      requestType: 'query',
    });

    const versions = result.listRPNVersions || [];

    logger.info(`Fetched ${versions.length} AGM Versions for RPN ${rpnId} from API`);
    return versions;
  } catch (error) {
    logger.error(`Error fetching AGM Versions for RPN ${rpnId}:`, { error });
    throw formatApiError('Failed to fetch AGM Versions', error);
  }
};

/**
 * Fetches only AGM assortments from AGM data
 * Used for targeted refetch after createOrUpdateAGMAssortments mutation
 * @param rpnId - The ID of the RPN
 * @returns Promise resolving to array of AGM assortments
 * @throws Error if API call fails
 */
export const fetchRPNAGMAssortmentsAPI = async (rpnId: number): Promise<RpnAssortment[]> => {
  try {
    logger.info(`Fetching AGM Assortments for RPN ${rpnId} from API`);

    const result = await fetchGraphQLRequest<{
      listRPNAssortments: RpnAssortment[];
    }>({
      query: listRPNAssortments,
      variables: { input: { rpn_id: rpnId } },
      requestName: 'listRPNAssortments',
      requestType: 'query',
    });

    const assortments = result.listRPNAssortments || [];

    logger.info(`Fetched ${assortments.length} AGM Assortments for RPN ${rpnId} from API`);
    return assortments;
  } catch (error) {
    logger.error(`Error fetching AGM Assortments for RPN ${rpnId}:`, { error });
    throw formatApiError('Failed to fetch AGM Assortments', error);
  }
};

/**
 * Fetches only AGM SKUs from AGM data
 * Used for targeted refetch when SKU data needs to be updated independently
 * @param rpnId - The ID of the RPN
 * @returns Promise resolving to array of SKUs
 * @throws Error if API call fails
 */
export const fetchRPNAGMSkusAPI = async (rpnId: number): Promise<Skus[]> => {
  try {
    logger.info(`Fetching AGM SKUs for RPN ${rpnId} from API`);

    const result = await fetchGraphQLRequest<{
      listRPNSkus: Skus[];
    }>({
      query: listRPNSkus,
      variables: { input: { rpn_id: rpnId } },
      requestName: 'listRPNSkus',
      requestType: 'query',
    });

    const skus = result.listRPNSkus || [];

    logger.info(`Fetched ${skus.length} AGM SKUs for RPN ${rpnId} from API`);
    return skus;
  } catch (error) {
    logger.error(`Error fetching AGM SKUs for RPN ${rpnId}:`, { error });
    throw formatApiError('Failed to fetch AGM SKUs', error);
  }
};

// ============================================================================
// Marketplace Operations
// ============================================================================

/**
 * Fetches all available marketplaces
 * Used in EditMarketplaceModal to display available marketplace options
 * @returns Promise resolving to array of marketplaces
 * @throws Error if API call fails
 */
export const fetchMarketplacesAPI = async (): Promise<Marketplace[]> => {
  try {
    logger.info('Fetching all available marketplaces from API');

    const result = await fetchGraphQLRequest<{
      listMarketplaces: Array<Marketplace | null> | null;
    }>({
      query: listMarketplaces,
      variables: {},
      requestName: 'listMarketplaces',
      requestType: 'query',
    });

    // Filter out null values and return clean array
    const marketplaces = (result.listMarketplaces || []).filter(
      (mp): mp is Marketplace => mp !== null && mp.marketplace !== null
    );

    logger.info(`Fetched ${marketplaces.length} marketplaces from API`);
    return marketplaces;
  } catch (error) {
    logger.error('Error fetching marketplaces:', { error });
    throw formatApiError('Failed to fetch marketplaces', error);
  }
};

/**
 * Requests a part number for a SKU
 * Calls the request_pn mutation with SKU ID and description (without PRODUCT_TYPE)
 * @param skuId - The ID of the SKU to create part number for
 * @param description - The description string (version attributes WITHOUT PRODUCT_TYPE)
 * @returns Promise resolving to the request result with part_number and sku_id
 * @throws Error if API call fails
 */
export const requestPartNumberAPI = async (
  skuId: number,
  description: string
): Promise<{ success: boolean; partNumber?: string; skuId?: number }> => {
  try {
    logger.info(`Requesting part number for SKU ${skuId}`, { description });

    const result = await fetchGraphQLRequest<{
      request_pn: RequestPartNumberStatus;
    }>({
      query: request_pn,
      variables: { input: { sku_id: skuId, description } },
      requestName: 'request_pn',
      requestType: 'mutation',
    });

    const response = result.request_pn;

    if (response.error || (response.statusCode && response.statusCode >= 400)) {
      throw new Error(response.error || `Failed with status code: ${response.statusCode}`);
    }

    logger.info(`Successfully requested part number for SKU ${skuId}`, {
      partNumber: response.body?.part_number,
    });

    return {
      success: true,
      partNumber: response.body?.part_number ?? undefined,
      skuId: response.body?.sku_id ?? undefined,
    };
  } catch (error) {
    logger.error(`Error requesting part number for SKU ${skuId}:`, { error });

    // request_pn is backed by AMIDS/Townsend. When the SKU has an attribute value
    // that isn't in the corresponding Townsend OptionSet (e.g. region 'AR' is not
    // in the LAB PACKOUT ASSEMBLY REGION set), the raw error is a stringified
    // Python dict that repeats the same failure across 4 case-variants of the
    // program name — plus three "Program was not found in Townsend" noise
    // entries. Run it through the parser to produce a single, human-friendly
    // message, and keep the raw payload on the thrown Error for logging /
    // debugging in the flashbar consumer.
    const rawMessage = getErrorMessage(error);
    const parsed = parseTownsendError(rawMessage);

    if (parsed.isValidationException) {
      logger.warn(`Townsend validation failure for SKU ${skuId}`, {
        failures: parsed.validationFailures,
        hadSuppressedNoise: parsed.hadSuppressedNoise,
      });
    }

    const wrapped = formatApiError('Failed to request part number', parsed.userMessage);
    // Attach the parsed detail so callers (e.g. flashbar) can decide how to display it.
    Object.assign(wrapped, {
      userMessage: parsed.userMessage,
      rawError: rawMessage,
      parsed,
    });
    throw wrapped;
  }
};

/**
 * Updates marketplace assignments for a SKU
 * Used in EditMarketplaceModal to add/remove additional marketplaces
 * @param input - The SKU ID and marketplace changes to apply
 * @returns Promise resolving to success status
 * @throws Error if API call fails
 */
export const updateSkuMarketplacesAPI = async (
  input: UpdateSkuMarketplacesInput
): Promise<{ success: boolean; lastSucceededIndex: number | null }> => {
  try {
    logger.info(`Updating marketplaces for SKU ${input.sku_id}`, {
      marketplaceCount: input.marketplaces.length,
      marketplaces: input.marketplaces,
    });

    const result = await fetchGraphQLRequest<{
      updateAGMSkuMarketplaces: UpdateSkuMarketplacesResponse;
    }>({
      query: updateAGMSkuMarketplaces,
      variables: { input },
      requestName: 'updateAGMSkuMarketplaces',
      requestType: 'mutation',
    });

    const response = result.updateAGMSkuMarketplaces;

    if (response.error || (response.statusCode && response.statusCode >= 400)) {
      throw new Error(response.error || `Failed with status code: ${response.statusCode}`);
    }

    logger.info(`Successfully updated marketplaces for SKU ${input.sku_id}`, {
      lastSucceededIndex: response.lastSucceededIndex,
    });

    return { success: true, lastSucceededIndex: response.lastSucceededIndex ?? null };
  } catch (error) {
    logger.error(`Error updating marketplaces for SKU ${input.sku_id}:`, { error });
    throw formatApiError('Failed to update SKU marketplaces', error);
  }
};

// ============================================================================
// SKU Operations
// ============================================================================

/**
 * Deactivates a SKU by ID
 * Used in DeactivateSkuModal to soft-delete a SKU
 * @param skuId - The ID of the SKU to deactivate
 * @returns Promise resolving to success status
 * @throws Error if API call fails
 */
export const deactivateSkuAPI = async (skuId: number): Promise<{ success: boolean }> => {
  try {
    logger.info(`Deactivating SKU ${skuId}`);

    const result = await fetchGraphQLRequest<{
      deactivateAGMSku: {
        body?: string | null;
        error?: string | null;
        lastSucceededIndex?: number | null;
        statusCode?: number | null;
      };
    }>({
      query: deactivateAGMSku,
      variables: { input: { sku_id: skuId } },
      requestName: 'deactivateAGMSku',
      requestType: 'mutation',
    });

    const response = result.deactivateAGMSku;

    if (response.error || (response.statusCode && response.statusCode >= 400)) {
      throw new Error(response.error || `Failed with status code: ${response.statusCode}`);
    }

    logger.info(`Successfully deactivated SKU ${skuId}`);
    return { success: true };
  } catch (error) {
    logger.error(`Error deactivating SKU ${skuId}:`, { error });
    throw formatApiError('Failed to deactivate SKU', error);
  }
};

/**
 * Reactivates a previously deactivated SKU by ID
 * Used in ReactivateSkuModal to restore a soft-deleted SKU
 * @param skuId - The ID of the SKU to reactivate
 * @returns Promise resolving to success status
 * @throws Error if API call fails
 */
export const reactivateSkuAPI = async (skuId: number): Promise<{ success: boolean }> => {
  try {
    logger.info(`Reactivating SKU ${skuId}`);

    const result = await fetchGraphQLRequest<{
      reactivateAGMSku: {
        body?: string | null;
        error?: string | null;
        lastSucceededIndex?: number | null;
        statusCode?: number | null;
      };
    }>({
      query: reactivateAGMSku,
      variables: { input: { sku_id: skuId } },
      requestName: 'reactivateAGMSku',
      requestType: 'mutation',
    });

    const response = result.reactivateAGMSku;

    if (response.error || (response.statusCode && response.statusCode >= 400)) {
      throw new Error(response.error || `Failed with status code: ${response.statusCode}`);
    }

    logger.info(`Successfully reactivated SKU ${skuId}`);
    return { success: true };
  } catch (error) {
    logger.error(`Error reactivating SKU ${skuId}:`, { error });
    throw formatApiError('Failed to reactivate SKU', error);
  }
};

/**
 * Unlocks ASIN creation for a blocked SKU
 * Sets notification_status to 'OVERRIDE' allowing part number creation without FPN
 * Used when a SKU is blocked (has blocked_ts) due to missing FPN
 * @param skuId - The ID of the SKU to unlock
 * @returns Promise resolving to success status and whether override email was sent
 * @throws Error if API call fails
 */
export const unlockSkuOverrideAPI = async (
  skuId: number
): Promise<{ success: boolean; overrideEmailSent?: boolean }> => {
  try {
    logger.info(`Unlocking ASIN creation for SKU ${skuId}`);

    const result = await fetchGraphQLRequest<{
      unlockSkuOverride: {
        body?: string | null;
        error?: string | null;
        override_email_sent?: boolean | null;
        statusCode?: number | null;
      };
    }>({
      query: unlockSkuOverride,
      variables: { input: { sku_id: skuId } },
      requestName: 'unlockSkuOverride',
      requestType: 'mutation',
    });

    const response = result.unlockSkuOverride;

    if (response.error || (response.statusCode && response.statusCode >= 400)) {
      throw new Error(response.error || `Failed with status code: ${response.statusCode}`);
    }

    logger.info(`Successfully unlocked ASIN creation for SKU ${skuId}`, {
      overrideEmailSent: response.override_email_sent,
    });

    return {
      success: true,
      overrideEmailSent: response.override_email_sent ?? undefined,
    };
  } catch (error) {
    logger.error(`Error unlocking ASIN creation for SKU ${skuId}:`, { error });
    throw formatApiError('Failed to unlock ASIN creation', error);
  }
};

/**
 * Updates SKU version type and assortments
 * Used in EditVersionModal to change SKU's version and sku_type
 * @param skuId - The ID of the SKU to update
 * @param versionId - The new version ID
 * @param skuType - The new SKU type
 * @param assortmentIds - Array of assortment IDs to assign to the SKU
 * @returns Promise resolving to success status
 * @throws Error if API call fails
 */
export const updateSkuVersionTypeAPI = async (
  skuId: number,
  versionId: number,
  skuType: string,
  assortmentIds: number[]
): Promise<{ success: boolean }> => {
  try {
    logger.info(`Updating SKU ${skuId} version/type`, { versionId, skuType, assortmentIds });

    const result = await fetchGraphQLRequest<{
      updateAGMSkuVersionType: {
        body?: string | null;
        error?: string | null;
        lastSucceededIndex?: number | null;
        statusCode?: number | null;
      };
    }>({
      query: updateAGMSkuVersionType,
      variables: {
        input: {
          sku_id: skuId,
          version_id: versionId,
          sku_type: skuType,
          assortment_ids: assortmentIds,
        },
      },
      requestName: 'updateAGMSkuVersionType',
      requestType: 'mutation',
    });

    const response = result.updateAGMSkuVersionType;

    if (response.error || (response.statusCode && response.statusCode >= 400)) {
      throw new Error(response.error || `Failed with status code: ${response.statusCode}`);
    }

    logger.info(`Successfully updated SKU ${skuId} version/type`);
    return { success: true };
  } catch (error) {
    logger.error(`Error updating SKU ${skuId} version/type:`, { error });
    throw formatApiError('Failed to update SKU version/type', error);
  }
};

/**
 * Updates SKU assortment assignments (add or remove)
 * Used in EditAssortmentsModal to modify which assortments are assigned to a SKU
 * @param skuId - The ID of the SKU to update
 * @param assortmentIds - Array of assortment IDs to add or remove
 * @param action - Whether to 'add' or 'delete' the assortments
 * @returns Promise resolving to success status
 * @throws Error if API call fails
 */
export const updateSkuAssortmentsAPI = async (
  skuId: number,
  assortmentIds: number[],
  action: 'add' | 'delete'
): Promise<{ success: boolean }> => {
  try {
    logger.info(`${action === 'add' ? 'Adding' : 'Removing'} assortments for SKU ${skuId}`, { assortmentIds });

    const result = await fetchGraphQLRequest<{
      updateAGMSkuAssortments: {
        body?: string | null;
        error?: string | null;
        lastSucceededIndex?: number | null;
        statusCode?: number | null;
      };
    }>({
      query: updateAGMSkuAssortments,
      variables: {
        input: {
          sku_id: skuId,
          assortment_ids: assortmentIds,
          action,
        },
      },
      requestName: 'updateAGMSkuAssortments',
      requestType: 'mutation',
    });

    const response = result.updateAGMSkuAssortments;

    if (response.error || (response.statusCode && response.statusCode >= 400)) {
      throw new Error(response.error || `Failed with status code: ${response.statusCode}`);
    }

    logger.info(`Successfully ${action === 'add' ? 'added' : 'removed'} assortments for SKU ${skuId}`);
    return { success: true };
  } catch (error) {
    logger.error(`Error ${action === 'add' ? 'adding' : 'removing'} assortments for SKU ${skuId}:`, { error });
    throw formatApiError(`Failed to ${action} SKU assortments`, error);
  }
};

/**
 * Creates new AGM SKUs
 * Used in AddSkuModal to create new SKUs with specified assortments
 * @param skus - Array of new SKU data to create
 * @returns Promise resolving to success status and lastSucceededIndex
 * @throws Error if API call fails
 */
export const createAGMSkuAPI = async (
  skus: AGMNewSkuInput[],
  createdByAlias?: string
): Promise<{ success: boolean; lastSucceededIndex: number | null }> => {
  try {
    logger.info(`Creating ${skus.length} new AGM SKU(s)`, {
      skuCount: skus.length,
      skus: skus.map((s) => ({
        version_id: s.version_id,
        sku_type: s.sku_type,
        assortment_count: s.assortment_ids.length,
      })),
    });

    const result = await fetchGraphQLRequest<{
      createAGMSku: {
        body?: string | null;
        error?: string | null;
        lastSucceededIndex?: number | null;
        statusCode?: number | null;
      };
    }>({
      query: createAGMSku,
      variables: { input: { skus } },
      requestName: 'createAGMSku',
      requestType: 'mutation',
    });

    const response = result.createAGMSku;

    if (response.error || (response.statusCode && response.statusCode >= 400)) {
      throw new Error(response.error || `Failed with status code: ${response.statusCode}`);
    }

    logger.info(`Successfully created ${skus.length} AGM SKU(s)`, {
      lastSucceededIndex: response.lastSucceededIndex,
    });

    // Fire-and-forget: log change history for SKU creation
    if (skus.length > 0 && skus[0].rpn_id) {
      logChangeHistory({
        entityType: ChangeEntityType.RPN,
        rpnId: skus[0].rpn_id!,
        productLineId: skus[0].product_line_id || undefined,
        sectionName: ChangeSectionName.AGM_SKUS,
        operationType: ChangeOperationType.CREATE,
        createdBy: createdByAlias || 'unknown',
        fieldChanges: skus.map((s) => ({
          field_name: 'sku',
          field_label: `Version ${s.version_id} / ${s.sku_type} (added)`,
          old_value: null,
          new_value: `SKU Type → ${s.sku_type}, Assortments → ${s.assortment_ids.length}`,
        })),
      });
    }

    return { success: true, lastSucceededIndex: response.lastSucceededIndex ?? null };
  } catch (error) {
    logger.error('Error creating AGM SKUs:', { error, skuCount: skus.length });
    throw formatApiError('Failed to create AGM SKUs', error);
  }
};

// ============================================================================
// AGM Program Version Operations (for Remap ASIN flow)
// ============================================================================

/**
 * Raw version row from listAGMProgramVersion API
 * This is the raw (unpacked) format - one row per attribute per version
 */
export interface RawVersionRow {
  attribute_type: string;
  attribute_value: string;
  version_id: number;
}

/**
 * FPN rule row from listRPNFPNRule API
 */
export interface FpnRuleRow {
  attribute_type: string;
  comments: string | null;
  concat_order: number;
  is_deprecated: boolean;
  ppls_id: string | null;
  product_line: string;
  rpn: string;
  verity_product_line_id: number;
  verity_rpn_id: number;
}

/**
 * Attribute list row from listAttributeListRPN/FPN API
 */
export interface AttributeListRow {
  asin_count: number;
  attribute_type: string;
  attribute_value: string;
  rpn?: string;
  verity_rpn_id?: number;
  verity_fpn_id?: number;
}

/**
 * Fetches raw AGM program versions for an RPN
 * Returns raw rows (one per attribute per version) - NOT pre-grouped like listRPNAGMVersions
 * Used in Remap ASIN flow to get raw version data
 * @param rpnId - The ID of the RPN
 * @returns Promise resolving to array of raw version rows
 * @throws Error if API call fails
 */
export const fetchAGMProgramVersionsAPI = async (rpnId: number): Promise<RawVersionRow[]> => {
  try {
    logger.info(`Fetching AGM Program Versions for RPN ${rpnId} from API`);

    const { listAGMProgramVersion } = await import('src/graphql/queries');

    const result = await fetchGraphQLRequest<{
      listAGMProgramVersion: RawVersionRow[];
    }>({
      query: listAGMProgramVersion,
      variables: { input: { rpn_id: rpnId } },
      requestName: 'listAGMProgramVersion',
      requestType: 'query',
    });

    const versions = result.listAGMProgramVersion || [];

    logger.info(`Fetched ${versions.length} raw AGM Program Version rows for RPN ${rpnId} from API`);
    return versions;
  } catch (error) {
    logger.error(`Error fetching AGM Program Versions for RPN ${rpnId}:`, { error });
    throw formatApiError('Failed to fetch AGM Program Versions', error);
  }
};

/**
 * Fetches FPN rule for an RPN
 * Used in Remap ASIN flow to determine FPN name generation rule
 * @param rpnId - The ID of the RPN
 * @returns Promise resolving to array of FPN rule rows
 * @throws Error if API call fails
 */
export const fetchRPNFPNRuleAPI = async (rpnId: number): Promise<FpnRuleRow[]> => {
  try {
    logger.info(`Fetching RPN FPN Rule for RPN ${rpnId} from API`);

    const { listRPNFPNRule } = await import('src/graphql/queries');

    // New API returns direct array instead of wrapped {body, error, statusCode}
    const result = await fetchGraphQLRequest<{
      listRPNFPNRule: FpnRuleRow[];
    }>({
      query: listRPNFPNRule,
      variables: { input: { rpn_id: rpnId } },
      requestName: 'listRPNFPNRule',
      requestType: 'query',
    });

    const rules = result.listRPNFPNRule || [];

    logger.info(`Fetched ${rules.length} FPN Rule rows for RPN ${rpnId} from API`);
    return rules;
  } catch (error) {
    logger.error(`Error fetching RPN FPN Rule for RPN ${rpnId}:`, { error });
    throw formatApiError('Failed to fetch RPN FPN Rule', error);
  }
};

/**
 * Fetches attribute list for an RPN
 * Used in Remap ASIN flow to get available attribute values
 * @param rpnId - The ID of the RPN
 * @returns Promise resolving to array of attribute list rows
 * @throws Error if API call fails
 */
export const fetchAttributeListRPNAPI = async (rpnId: number): Promise<AttributeListRow[]> => {
  try {
    logger.info(`Fetching Attribute List for RPN ${rpnId} from API`);

    const { listAttributeListRPN } = await import('src/graphql/queries');

    // New API returns direct array instead of wrapped {body, error, statusCode}
    const result = await fetchGraphQLRequest<{
      listAttributeListRPN: AttributeListRow[];
    }>({
      query: listAttributeListRPN,
      variables: { input: { rpn_id: rpnId } },
      requestName: 'listAttributeListRPN',
      requestType: 'query',
    });

    const attributes = result.listAttributeListRPN || [];

    logger.info(`Fetched ${attributes.length} Attribute List rows for RPN ${rpnId} from API`);
    return attributes;
  } catch (error) {
    logger.error(`Error fetching Attribute List for RPN ${rpnId}:`, { error });
    throw formatApiError('Failed to fetch Attribute List for RPN', error);
  }
};

/**
 * Fetches attribute list for an FPN
 * Used in Remap ASIN flow to filter versions by FPN
 * @param fpnId - The ID of the FPN
 * @returns Promise resolving to array of attribute list rows
 * @throws Error if API call fails
 */
export const fetchAttributeListFPNAPI = async (fpnId: number): Promise<AttributeListRow[]> => {
  try {
    logger.info(`Fetching Attribute List for FPN ${fpnId} from API`);

    const { listAttributeListFPN } = await import('src/graphql/queries');

    // New API returns direct array instead of wrapped {body, error, statusCode}
    const result = await fetchGraphQLRequest<{
      listAttributeListFPN: AttributeListRow[];
    }>({
      query: listAttributeListFPN,
      variables: { input: { fpn_id: fpnId } },
      requestName: 'listAttributeListFPN',
      requestType: 'query',
    });

    const attributes = result.listAttributeListFPN || [];

    logger.info(`Fetched ${attributes.length} Attribute List rows for FPN ${fpnId} from API`);
    return attributes;
  } catch (error) {
    logger.error(`Error fetching Attribute List for FPN ${fpnId}:`, { error });
    throw formatApiError('Failed to fetch Attribute List for FPN', error);
  }
};

/**
 * A single (device FPN -> accessory FPN) compatibility pair for a device RPN, computed server-side
 * as the reverse of the accessory-side COMPATIBLE PROGRAMS attribute.
 */
export interface CompatibleProgramPair {
  device_fpn_id: number;
  device_fpn_alias: string;
  accessory_fpn_id: number;
  accessory_fpn_alias: string;
}

/**
 * In-flight de-duplication for the reverse lookup. A single RPN page mounts multiple consumers of
 * this query (the Compatibility Mapping table + the read-only/derived attribute), which would
 * otherwise fire identical concurrent requests for the same rpnId. We cache the in-flight promise
 * per rpnId and hand the same one to concurrent callers, then drop it once it settles — so a later
 * load (e.g. after a save/refetch) still fetches fresh data. This is a request coalescer, not a
 * result cache: there is no staleness risk.
 */
const inFlightReverseRequests = new Map<number, Promise<CompatibleProgramPair[]>>();

/**
 * Fetches the reverse compatible programs for a device RPN (reverse lookup of COMPATIBLE PROGRAMS).
 *
 * Concurrent calls for the same rpnId share a single in-flight request (see
 * `inFlightReverseRequests`). Fails open: if the backend query is not deployed yet (or errors),
 * returns an empty list so the device-side table simply hides itself rather than breaking the page.
 *
 * @param rpnId - The device RPN id
 * @returns Promise resolving to (device FPN, accessory FPN) pairs; empty on error.
 */
export const fetchCompatibleProgramsReverseAPI = (rpnId: number): Promise<CompatibleProgramPair[]> => {
  const existing = inFlightReverseRequests.get(rpnId);
  if (existing) {
    return existing;
  }

  const request = (async (): Promise<CompatibleProgramPair[]> => {
    try {
      logger.info(`Fetching reverse compatible programs for RPN ${rpnId} from API`);

      const { listCompatibleProgramsReverse } = await import('src/graphql/queries');

      const result = await fetchGraphQLRequest<{
        listCompatibleProgramsReverse: CompatibleProgramPair[];
      }>({
        query: listCompatibleProgramsReverse,
        variables: { input: { rpn_id: rpnId } },
        requestName: 'listCompatibleProgramsReverse',
        requestType: 'query',
      });

      const pairs = result.listCompatibleProgramsReverse || [];
      logger.info(`Fetched ${pairs.length} reverse compatible-program pairs for RPN ${rpnId} from API`);
      return pairs;
    } catch (error) {
      // Fail open: log and return empty so the derived side hides until the backend exists.
      logger.error(`Error fetching reverse compatible programs for RPN ${rpnId} (failing open with empty list):`, {
        error,
      });
      return [];
    }
  })();

  inFlightReverseRequests.set(rpnId, request);
  // Drop the entry once settled so subsequent loads re-fetch (coalescer, not a cache).
  void request.finally(() => {
    if (inFlightReverseRequests.get(rpnId) === request) {
      inFlightReverseRequests.delete(rpnId);
    }
  });

  return request;
};

/** The minimal SKU fields the compatibility tables need (channel + country per FPN). */
export interface FpnSkuChannelCountry {
  channel: string | null;
  country: string | null;
}

/**
 * Fetches an FPN's SKUs (channel + country) via the existing listFPNSkus query. Used by the
 * compatibility tables to derive per-FPN Online / Offline country sets.
 *
 * Fails open: returns an empty list on error so the table degrades gracefully rather than breaking.
 *
 * @param fpnId - The FPN id
 * @returns Promise resolving to the FPN's SKU channel/country rows; empty on error.
 */
export const fetchFPNSkusAPI = async (fpnId: number): Promise<FpnSkuChannelCountry[]> => {
  try {
    logger.info(`Fetching SKUs for FPN ${fpnId} from API`);

    const { listFPNSkus } = await import('src/graphql/queries');

    const result = await fetchGraphQLRequest<{
      listFPNSkus: FpnSkuChannelCountry[];
    }>({
      query: listFPNSkus,
      variables: { input: { fpn_id: fpnId } },
      requestName: 'listFPNSkus',
      requestType: 'query',
    });

    return result.listFPNSkus || [];
  } catch (error) {
    logger.error(`Error fetching SKUs for FPN ${fpnId} (failing open with empty list):`, { error });
    return [];
  }
};

// ============================================================================
// ASIN Remap Operations
// ============================================================================

/**
 * Remaps an ASIN to a new FPN/version/assortments
 * Used in RemapAsinSplitPanel to change the target mapping for an existing SKU
 * @param input - The remap input containing current data, target data, and authorization info
 * @returns Promise resolving to success status
 * @throws Error if API call fails
 */
export const remapAsinAPI = async (input: {
  acknowledgement: boolean;
  current_data: {
    asin: string;
    assortment_ids?: Array<number | null> | null;
    fpn: string;
    fpn_id: string;
    part_number?: string | null;
    rpn: string;
    rpn_id: string;
    version_id?: number | null;
  };
  justification?: string | null;
  reviewer?: string | null;
  sku_id: number;
  target_data: {
    assortments: Array<{
      channel: string;
      country: string;
      sku_type: string;
    }>;
    fpn: string;
    fpn_id?: string | null;
    rpn: string;
    rpn_id: string;
    version: {
      attributes: Array<{
        attribute_type: string;
        attribute_value: string;
      }>;
      version_id?: string | null;
    };
  };
}): Promise<{ success: boolean }> => {
  try {
    logger.info(`Remapping ASIN for SKU ${input.sku_id}`, {
      currentFpn: input.current_data.fpn,
      targetFpn: input.target_data.fpn,
      targetRpnId: input.target_data.rpn_id,
    });

    // Import mutation dynamically to avoid circular dependency issues
    const { remapAGMAsin } = await import('src/graphql/mutations');

    const result = await fetchGraphQLRequest<{
      remapAGMAsin: {
        body?: string | null;
        error?: string | null;
        lastSucceededIndex?: number | null;
        statusCode?: number | null;
      };
    }>({
      query: remapAGMAsin,
      variables: { input },
      requestName: 'remapAGMAsin',
      requestType: 'mutation',
    });

    const response = result.remapAGMAsin;

    if (response.error || (response.statusCode && response.statusCode >= 400)) {
      throw new Error(response.error || `Failed with status code: ${response.statusCode}`);
    }

    logger.info(`Successfully remapped ASIN for SKU ${input.sku_id}`);
    return { success: true };
  } catch (error) {
    logger.error(`Error remapping ASIN for SKU ${input.sku_id}:`, { error });
    throw formatApiError('Failed to remap ASIN', error);
  }
};
